import { toast } from 'sonner';
import {
  DEFAULT_DOSSIER_REGISTRY_SETTINGS,
  REGISTRY_FILTER_KEYS,
  REGISTRY_FILTER_LABELS,
  type DossierRegistrySettings,
  type RegistryFilterKey,
} from '../../lib/dossiers/registrySettings';
import {
  useDossierRegistrySettings,
  useUpdateDossierRegistrySettings,
} from '../../hooks/queries/useCabinetSettings';
import { cn } from '../../lib/cn';

export function DossierRegistrySection() {
  const { data: settings = DEFAULT_DOSSIER_REGISTRY_SETTINGS } = useDossierRegistrySettings();
  const update = useUpdateDossierRegistrySettings();

  function save(next: DossierRegistrySettings) {
    update.mutate(next, {
      onError: (err) => {
        toast.error(
          err instanceof Error ? err.message : 'Impossible d’enregistrer le réglage',
        );
      },
    });
  }

  function toggleFilter(key: RegistryFilterKey, checked: boolean) {
    const filters = REGISTRY_FILTER_KEYS.filter((k) =>
      k === key ? checked : settings.filters.includes(k),
    );
    save({ ...settings, filters });
  }

  return (
    <section className="rounded-cime border border-neutral-200 bg-white p-4 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-cime-text-primary">Registre des dossiers</h2>
        <p className="text-sm text-cime-text-muted mt-1">
          Ce que tout le cabinet voit sur la page Dossiers.
        </p>
      </div>
      <Check
        id="dossier-registry-board"
        label="Vue en colonnes (Kanban)"
        hint="Décochée : le sélecteur Liste / Kanban disparaît, seule la liste reste."
        checked={settings.board}
        disabled={update.isPending}
        onChange={(checked) => save({ ...settings, board: checked })}
      />
      <div className="space-y-2">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-cime-text-muted">
          Filtres affichés
        </h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {REGISTRY_FILTER_KEYS.map((key) => (
            <Check
              key={key}
              id={`dossier-registry-filter-${key}`}
              label={REGISTRY_FILTER_LABELS[key]}
              hint={key === 'frise' ? 'Le parcours du dossier, avec le filtre Étape.' : undefined}
              checked={settings.filters.includes(key)}
              disabled={update.isPending}
              onChange={(checked) => toggleFilter(key, checked)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Check({
  id, label, hint, checked, disabled, onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-center gap-3 rounded-md border p-3 cursor-pointer',
        checked ? 'border-cime-primary bg-cime-primary/5' : 'border-neutral-200',
      )}
    >
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 accent-cime-primary"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block text-sm font-medium text-cime-text-primary">{label}</span>
        {hint ? <span className="block text-xs text-cime-text-muted">{hint}</span> : null}
      </span>
    </label>
  );
}
